import { ForwardRefRenderFunction, forwardRef, useEffect, useImperativeHandle, useMemo, useState } from 'react'

import { PointCloudOctree } from '@datalabs-jp/three-loader'
import { InspectionArea } from '@editor/interfaces/inspection'
import CircleAnchor from '@editor/pages/projects/editor/objects/EditorCircleAnchor'
import { focusCamera, getCenterAndRadius } from '@editor/services/Editor'
import { getCentroidOfPolygon3d, resetPCD } from '@editor/services/Points'
import { withPromiseContained } from '@editor/services/Util'
import { ArcballControls } from '@editor/services/threejs'
import { RootState, useAppDispatch } from '@editor/store/app'
import { useSelector } from 'react-redux'
import { Points, Vector3 } from 'three'

import usePCDLoader from '../../hooks/usePCDLoader'
import { ANCHOR_COLORS, SceneEventHandlers, Steps, Tools } from '../definitions'
import { usePointSelection } from '../hooks/usePointSelection'
import { setLeftProgress, setRightProgress, setTransformMatrix } from '../store'
import { computeTransformMatrix, getObjectAreaId } from '../utils'
import { MainSceneProps } from './MainScene'

interface FineTuneSceneProps extends MainSceneProps {
  leftInspectionArea: InspectionArea | null
  rightInspectionArea: InspectionArea | undefined
}

const FineTuneSceneFn: ForwardRefRenderFunction<SceneEventHandlers, FineTuneSceneProps> = (
  { sceneRef, controlsRef, camera, leftInspectionArea, rightInspectionArea, potree },
  ref,
) => {
  // Store
  const dispatch = useAppDispatch()
  const currentStep = useSelector((state: RootState) => state.pcdAlignment.currentStep)
  const leftAnchors = useSelector((state: RootState) => state.pcdAlignment.leftAnchors)
  const rightAnchors = useSelector((state: RootState) => state.pcdAlignment.rightAnchors)
  const currentTool = useSelector((state: RootState) => state.pcdAlignment.currentTool)

  // States
  const [leftPointCloud, setLeftPointCloud] = useState<Points | PointCloudOctree>()
  const [rightPointCloud, setRightPointCloud] = useState<Points | PointCloudOctree>()
  const [initCompleted, setInitCompleted] = useState(false)
  const [center, setCenter] = useState<Vector3 | null>(null)
  const [radius, setRadius] = useState(0)

  // Hooks
  const { loadPCD } = usePCDLoader(potree)
  const { projectPoint } = usePointSelection(
    [leftPointCloud, rightPointCloud].filter(Boolean) as (Points | PointCloudOctree)[],
    camera,
  )

  /**
   * Compute transform matrix to align left PCD to right PCD.
   */
  const matrix = useMemo(() => {
    const leftPCDAreaId = leftPointCloud ? getObjectAreaId(leftPointCloud) : null
    const rightPCDAreaId = rightPointCloud ? getObjectAreaId(rightPointCloud) : null

    if (
      currentStep !== Steps.FineTuneOrSave ||
      leftAnchors.length < 3 ||
      rightAnchors.length < 3 ||
      !leftPointCloud ||
      !rightPointCloud ||
      leftInspectionArea?.inspection_area_id !== leftPCDAreaId ||
      rightInspectionArea?.inspection_area_id !== rightPCDAreaId
    )
      return null
    return computeTransformMatrix(
      leftAnchors.map((anchor) => new Vector3(...anchor)),
      rightAnchors.map((anchor) => new Vector3(...anchor)),
    )
  }, [leftAnchors, rightAnchors, currentStep, leftPointCloud, rightPointCloud, leftInspectionArea, rightInspectionArea])

  /**
   * Transform left anchors to the right point cloud.
   */
  const leftAnchorsTransformed = useMemo(() => {
    if (!matrix) return leftAnchors

    return leftAnchors.map((anchor) => {
      const vec = new Vector3(...anchor)
      vec.applyMatrix4(matrix)
      return vec.toArray()
    })
  }, [leftAnchors, matrix])

  /**
   * Save the transform matrix to the store.
   */
  useEffect(() => {
    if (!matrix) return

    dispatch(setTransformMatrix(matrix.toArray()))
  }, [matrix, dispatch])

  /**
   * If the step is not Fine tune and there's not enough anchors, reset.
   */
  useEffect(() => {
    if ((leftAnchors.length < 3 || rightAnchors.length < 3) && currentStep !== Steps.FineTuneOrSave) {
      setInitCompleted(false)
      setLeftPointCloud(undefined)
      setRightPointCloud(undefined)
    }
  }, [leftAnchors, rightAnchors, currentStep])

  /**
   * Calculate box and center with combined bounding box of both PCDs.
   */
  useEffect(() => {
    if (rightAnchors.length < 3 || !leftPointCloud || !rightPointCloud || !initCompleted) return

    const { radius: rds } = getCenterAndRadius([leftPointCloud, rightPointCloud])
    const rightV3 = rightAnchors.map((anchor) => new Vector3(...anchor))
    const ctr = getCentroidOfPolygon3d(rightV3)
    setCenter(ctr)
    setRadius(rds)
  }, [leftPointCloud, rightPointCloud, rightAnchors, initCompleted])

  /**
   * Apply matrix to the left point cloud.
   */
  useEffect(() => {
    if (
      !matrix ||
      !leftPointCloud ||
      !rightPointCloud ||
      currentStep !== Steps.FineTuneOrSave ||
      currentTool !== Tools.PCDAlignment
    ) {
      return
    }

    resetPCD(rightPointCloud)
    resetPCD(leftPointCloud)
    leftPointCloud.applyMatrix4(matrix)

    setInitCompleted(true)
  }, [matrix, leftPointCloud, rightPointCloud, currentStep, currentTool])

  /**
   * Load PCD files.
   */
  useEffect(() => {
    if (
      !leftInspectionArea ||
      !rightInspectionArea ||
      currentStep !== Steps.FineTuneOrSave ||
      currentTool !== Tools.PCDAlignment
    )
      return

    setLeftPointCloud(undefined)
    setRightPointCloud(undefined)
    setInitCompleted(false)

    // Load both left and right PCD files.
    // These should load immediately due to cache.
    void withPromiseContained(async () => {
      const leftPoints = await loadPCD({ inspectionArea: leftInspectionArea })
      const rightPoints = await loadPCD({ inspectionArea: rightInspectionArea })

      if (leftPoints) {
        dispatch(setLeftProgress(null))
        setLeftPointCloud(leftPoints)
      }

      if (rightPoints) {
        dispatch(setRightProgress(null))
        setRightPointCloud(rightPoints)
      }
    })
  }, [leftInspectionArea, rightInspectionArea, currentStep, currentTool, sceneRef, loadPCD, dispatch])

  /**
   * Handle scene cleanup.
   */
  useEffect(() => {
    if (!leftInspectionArea || !rightInspectionArea) return

    if (currentStep !== Steps.FineTuneOrSave || currentTool !== Tools.PCDAlignment) {
      setLeftPointCloud(undefined)
      setRightPointCloud(undefined)
      setInitCompleted(false)
    }
  }, [currentStep, currentTool, leftInspectionArea, rightInspectionArea])

  /**
   * Handle user interaction event.
   */
  useImperativeHandle(ref, () => ({
    onFocus: (ev) => {
      const point = projectPoint(ev)
      if (!controlsRef.current || !point) return
      focusCamera(controlsRef.current, camera, point, Infinity)
    },
  }))

  useEffect(() => {
    const canvasBox: HTMLCanvasElement | null = document.querySelector('#canvasBox canvas')
    if (!sceneRef.current || !canvasBox || !leftPointCloud || !rightPointCloud || !center || controlsRef.current)
      return () => null

    controlsRef.current = new ArcballControls(camera, canvasBox, sceneRef.current)
    controlsRef.current.enableFocus = false
    controlsRef.current.enableAnimations = false
    controlsRef.current.activateGizmos(false)
    controlsRef.current.setGizmosVisible(false)

    camera.position.copy(center.clone().setZ(center.z + radius))
    focusCamera(controlsRef.current, camera, center, Infinity)

    return () => {
      controlsRef.current?.dispose()
      controlsRef.current = null
    }
  }, [controlsRef, camera, sceneRef, leftPointCloud, rightPointCloud, center, radius])

  useEffect(() => {
    if (!controlsRef.current) return
    controlsRef.current.enabled = currentStep === Steps.FineTuneOrSave && currentTool === Tools.PCDAlignment
  }, [currentStep, currentTool, controlsRef])

  return (
    <scene ref={sceneRef}>
      {leftPointCloud && initCompleted ? (
        <primitive key={`finetune-left-${leftPointCloud.name}`} object={leftPointCloud} />
      ) : null}
      {rightPointCloud && initCompleted ? (
        <primitive key={`finetune-right-${rightPointCloud.name}`} object={rightPointCloud} />
      ) : null}

      {leftAnchorsTransformed.map((anchor, idx) => (
        <CircleAnchor
          id={`anchor-left-${idx}`}
          key={anchor.join()}
          point={anchor}
          color={ANCHOR_COLORS[idx]}
          customCamera={camera}
        />
      ))}

      {rightAnchors.map((anchor, idx) => (
        <CircleAnchor
          id={`anchor-right-${idx}`}
          key={anchor.join()}
          point={anchor}
          color={ANCHOR_COLORS[idx]}
          customCamera={camera}
        />
      ))}

      <ambientLight intensity={Math.PI} />
    </scene>
  )
}

const FineTuneScene = forwardRef(FineTuneSceneFn)
export default FineTuneScene
